import React from 'react';
import { Box, Chip, Typography } from '@drone-js/core';
import { useSelector } from 'react-redux';
import { selectTopSubcategoriesFromBootstrap } from 'store/slice/bootstrap';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesPopularFiltersOptionClickEventActivitiesFieldsV3,
    ActivitiesPopularFiltersContainerSeenEventActivitiesFieldsV3,
    ActivitiesPopularFiltersOptionHoverEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesPopularFiltersOptionClickEventActivitiesManifestV3,
    ActivitiesPopularFiltersContainerSeenEventActivitiesManifestV3,
    ActivitiesPopularFiltersOptionHoverEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { InView } from 'react-intersection-observer';
import { SubcategoryId } from 'core/type/Subcategory.types';
import { SubcategoryValueFilter } from 'component/page/search/SortAndFilter/CategoryFilter/CategoryFilter.types';
import FilterTooltipWrapper, { getTruncatedText } from 'component/page/search/SortAndFilter/Filter/FilterTooltipWrapper';
import { getTopSubcategoryFilterMap } from 'component/page/search/SortAndFilter/CategoryWithSubcategoryFilter/Subcategory.utils';
import { SortAndFilterContentVariant } from 'component/page/search/SortAndFilter/SortAndFilterContent.selectors';

export interface Props {
    title: string;
    variant: SortAndFilterContentVariant;
    subcategoryFilter: SubcategoryValueFilter[];
    onSubcategoryFilterChange: (subcategoryId: SubcategoryId, selected: boolean) => void;
}

const PopularFilter: React.FC<Props> = React.memo((props) => {
    const topSubcategories = useSelector(selectTopSubcategoriesFromBootstrap);
    const isSidebar = props.variant === SortAndFilterContentVariant.Sidebar;
    const isFilterBottomSheet = props.variant === SortAndFilterContentVariant.CategoryBottomSheet;

    if (!topSubcategories || topSubcategories.length === 0) {
        return null;
    }

    const topSubcategoryFilter = getTopSubcategoryFilterMap(topSubcategories, props.subcategoryFilter).map((item) => item.filterInfo);

    const onClickTopSubcategoriesItem = (subcategoryFilter: SubcategoryValueFilter, index: number) => {
        trackElementEvent<ActivitiesPopularFiltersOptionClickEventActivitiesFieldsV3>(ActivitiesPopularFiltersOptionClickEventActivitiesManifestV3.ids, {
            is_sidebar: isSidebar,
            is_filter_bottom_sheet: isFilterBottomSheet,
            action_element_value: subcategoryFilter.label,
            action_element_index: index + 1,
        });
        props.onSubcategoryFilterChange(subcategoryFilter.id, !subcategoryFilter.showAsSelected);
    };

    const onSeen = (inView: boolean) => {
        if (inView) {
            trackElementEvent<ActivitiesPopularFiltersContainerSeenEventActivitiesFieldsV3>(ActivitiesPopularFiltersContainerSeenEventActivitiesManifestV3.ids, {
                is_sidebar: isSidebar,
                is_filter_bottom_sheet: isFilterBottomSheet,
            });
        }
    };

    const onHover = (subcategoryFilter: SubcategoryValueFilter, index: number) => {
        trackElementEvent<ActivitiesPopularFiltersOptionHoverEventActivitiesFieldsV2>(ActivitiesPopularFiltersOptionHoverEventActivitiesManifestV2.ids, {
            is_sidebar: isSidebar,
            is_filter_bottom_sheet: isFilterBottomSheet,
            action_element_value: subcategoryFilter.label,
            action_element_index: index + 1,
        });
    };

    if (topSubcategoryFilter.length === 0) {
        return null;
    }

    return (
        <InView onChange={onSeen} triggerOnce={true} threshold={1}>
            <Box data-element-name="activities-popular-filters-container" data-testid="activities-popular-filters-container" pb="24" px="16">
                <Typography aria-label={props.title} data-testid="activities-popular-filters-title" variant="heading-default" mt="0" mb="16">
                    {props.title}
                </Typography>
                <Box display="flex" flexWrap="wrap">
                    {topSubcategoryFilter.map((item, key) => (
                        <FilterTooltipWrapper title={item.label} key={key}>
                            <Chip
                                label={getTruncatedText(item.label)}
                                data-element-name="activities-popular-filters-option"
                                data-testid="activities-popular-filters-option"
                                data-element-index={key + 1}
                                onClick={() => onClickTopSubcategoriesItem(item, key)}
                                onMouseEnter={() => onHover(item, key)}
                                selection={item.showAsSelected ? 'selected' : 'unselected'}
                                mb={key === topSubcategoryFilter.length - 1 ? '0' : '12'}
                                me={key === topSubcategoryFilter.length - 1 ? '0' : '8'}
                                minWidth="content-fit"
                                size="s"
                                aria-label={item.label}
                            />
                        </FilterTooltipWrapper>
                    ))}
                </Box>
            </Box>
        </InView>
    );
});
PopularFilter.displayName = 'PopularFilter';

export default PopularFilter;
